import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { NoWebshopState, WebshopErrorState } from './_webshopNotice';
import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    InputBase,
    MenuItem,
    Paper,
    Popper,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { DataGridPro, useGridApiContext } from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Colors from '../../library/Colors';
import SftGlobalData from '../../library/SftGlobalData';
import i18n from '../../i18n';
import { registerUnsavedCheck, unregisterUnsavedCheck } from '../../library/UnsavedChangesGuard';

// ============================================================
// Mock data — kept temporarily until everything below is migrated to live
// data via the auth-backend proxy. Currently only SIZE_GROUPS is referenced.
// ============================================================

const MOCK_BRANDS = [
    { id: 'NIK', name: 'Nike',        slug: 'nike',        display_name: 'Nike',        external_supplier: null,    explore_order_priority: 10,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-18 09:32' },
    { id: 'ADI', name: 'Adidas',      slug: 'adidas',      display_name: 'adidas',      external_supplier: null,    explore_order_priority: 20,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-18 09:32' },
    { id: 'PUM', name: 'Puma',        slug: 'puma',        display_name: 'PUMA',        external_supplier: null,    explore_order_priority: 30,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-17 15:10' },
    { id: 'NBA', name: 'New Balance', slug: 'new-balance', display_name: 'New Balance', external_supplier: 'NBUSA', explore_order_priority: 40,    use_external_stock: 1, fetch_not_in_stock_products: 1, visible: 0, logo_visible: 1, updated_at: '2026-04-16 11:45' },
    { id: 'RBK', name: 'Reebok',      slug: 'reebok',      display_name: 'Reebok',      external_supplier: null,    explore_order_priority: 50,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-15 17:22' },
    { id: 'UAR', name: 'Under Armour',slug: 'under-armour',display_name: 'Under Armour',external_supplier: null,    explore_order_priority: 99999, use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 0, updated_at: '2026-04-12 08:04' },
    { id: 'ASC', name: 'Asics',       slug: 'asics',       display_name: 'ASICS',       external_supplier: null,    explore_order_priority: 60,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-11 14:18' },
    { id: 'HKA', name: 'Hoka',        slug: 'hoka',        display_name: 'HOKA',        external_supplier: null,    explore_order_priority: 70,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-10 10:00' },
];

const MOCK_CATEGORIES = [
    { id: 'M000', level: 1, name: 'Men',      loc_nl: 'Heren',       loc_en: 'Men',      loc_fr: 'Hommes',    order_index: 10, explore_order_priority: 10, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'W000', level: 1, name: 'Women',    loc_nl: 'Dames',       loc_en: 'Women',    loc_fr: 'Femmes',    order_index: 20, explore_order_priority: 20, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'K000', level: 1, name: 'Kids',     loc_nl: 'Kinderen',    loc_en: 'Kids',     loc_fr: 'Enfants',   order_index: 30, explore_order_priority: 30, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'MSH0', level: 2, name: 'Shoes',    loc_nl: 'Schoenen',    loc_en: 'Shoes',    loc_fr: 'Chaussures',order_index: 11, explore_order_priority: 11, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'MSR0', level: 2, name: 'Shirts',   loc_nl: 'Shirts',      loc_en: 'Shirts',   loc_fr: 'Chemises',  order_index: 12, explore_order_priority: 12, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'WDR0', level: 2, name: 'Dresses',  loc_nl: 'Jurken',      loc_en: 'Dresses',  loc_fr: 'Robes',     order_index: 21, explore_order_priority: 21, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'WPT0', level: 2, name: 'Pants',    loc_nl: 'Broeken',     loc_en: 'Pants',    loc_fr: 'Pantalons', order_index: 22, explore_order_priority: 22, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'MSNK', level: 3, name: 'Sneakers', loc_nl: 'Sneakers',    loc_en: 'Sneakers', loc_fr: 'Baskets',   order_index: 111,explore_order_priority: 111,visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'MBT0', level: 3, name: 'Boots',    loc_nl: 'Laarzen',     loc_en: 'Boots',    loc_fr: 'Bottes',    order_index: 112,explore_order_priority: 112,visible: 0, updated_at: '2026-02-14 16:30' },
];

const MOCK_COLORS = [
    { id: 'BLK',  name: 'Black',  hex: '#000000', loc_nl: 'Zwart',  loc_en: 'Black',  loc_fr: 'Noir',   display_name: 'Black',  visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'WHT',  name: 'White',  hex: '#FFFFFF', loc_nl: 'Wit',    loc_en: 'White',  loc_fr: 'Blanc',  display_name: 'White',  visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'RED',  name: 'Red',    hex: '#E53935', loc_nl: 'Rood',   loc_en: 'Red',    loc_fr: 'Rouge',  display_name: 'Red',    visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'BLU',  name: 'Blue',   hex: '#1976D2', loc_nl: 'Blauw',  loc_en: 'Blue',   loc_fr: 'Bleu',   display_name: 'Blue',   visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'GRN',  name: 'Green',  hex: '#43A047', loc_nl: 'Groen',  loc_en: 'Green',  loc_fr: 'Vert',   display_name: 'Green',  visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'YLW',  name: 'Yellow', hex: '#FDD835', loc_nl: 'Geel',   loc_en: 'Yellow', loc_fr: 'Jaune',  display_name: 'Yellow', visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'GRY',  name: 'Grey',   hex: '#757575', loc_nl: 'Grijs',  loc_en: 'Grey',   loc_fr: 'Gris',   display_name: 'Grey',   visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'NVY',  name: 'Navy',   hex: '#0D47A1', loc_nl: 'Marine', loc_en: 'Navy',   loc_fr: 'Marine', display_name: 'Navy',   visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'BEI',  name: 'Beige',  hex: '#D7C0A3', loc_nl: 'Beige',  loc_en: 'Beige',  loc_fr: 'Beige',  display_name: 'Beige',  visible: 0, updated_at: '2026-02-01 09:15' },
    { id: 'PNK',  name: 'Pink',   hex: '#EC407A', loc_nl: 'Roze',   loc_en: 'Pink',   loc_fr: 'Rose',   display_name: 'Pink',   visible: 1, updated_at: '2026-03-15 12:00' },
];

const MOCK_COUNTRIES = [
    { id: 1,  sft_id: 'BE', iso: 'BE', iso_3: 'BEL', iso_num: 56,  name: 'Belgium',     loc_nl: 'België',              loc_en: 'Belgium',     loc_fr: 'Belgique',    currency_code: 'EUR', phone_code: '+32',  prefered: 1, shipping_costs: 5.95, shipping_free_from: 50,  languages: 'nl,fr', visible: 1 },
    { id: 2,  sft_id: 'NL', iso: 'NL', iso_3: 'NLD', iso_num: 528, name: 'Netherlands', loc_nl: 'Nederland',           loc_en: 'Netherlands', loc_fr: 'Pays-Bas',    currency_code: 'EUR', phone_code: '+31',  prefered: 1, shipping_costs: 7.50, shipping_free_from: 75,  languages: 'nl',    visible: 1 },
    { id: 3,  sft_id: 'FR', iso: 'FR', iso_3: 'FRA', iso_num: 250, name: 'France',      loc_nl: 'Frankrijk',           loc_en: 'France',      loc_fr: 'France',      currency_code: 'EUR', phone_code: '+33',  prefered: 1, shipping_costs: 9.95, shipping_free_from: 100, languages: 'fr',    visible: 1 },
    { id: 4,  sft_id: 'DE', iso: 'DE', iso_3: 'DEU', iso_num: 276, name: 'Germany',     loc_nl: 'Duitsland',           loc_en: 'Germany',     loc_fr: 'Allemagne',   currency_code: 'EUR', phone_code: '+49',  prefered: 0, shipping_costs: 9.95, shipping_free_from: 100, languages: 'de',    visible: 1 },
    { id: 5,  sft_id: 'LU', iso: 'LU', iso_3: 'LUX', iso_num: 442, name: 'Luxembourg',  loc_nl: 'Luxemburg',           loc_en: 'Luxembourg',  loc_fr: 'Luxembourg',  currency_code: 'EUR', phone_code: '+352', prefered: 0, shipping_costs: 7.50, shipping_free_from: 75,  languages: 'fr,de', visible: 1 },
    { id: 6,  sft_id: 'GB', iso: 'GB', iso_3: 'GBR', iso_num: 826, name: 'United Kingdom', loc_nl: 'Verenigd Koninkrijk', loc_en: 'United Kingdom', loc_fr: 'Royaume-Uni', currency_code: 'GBP', phone_code: '+44', prefered: 0, shipping_costs: 15.00, shipping_free_from: 150, languages: 'en', visible: 0 },
    { id: 7,  sft_id: 'ES', iso: 'ES', iso_3: 'ESP', iso_num: 724, name: 'Spain',       loc_nl: 'Spanje',              loc_en: 'Spain',       loc_fr: 'Espagne',     currency_code: 'EUR', phone_code: '+34',  prefered: 0, shipping_costs: 12.95,shipping_free_from: 125, languages: 'es',    visible: 1 },
    { id: 8,  sft_id: 'IT', iso: 'IT', iso_3: 'ITA', iso_num: 380, name: 'Italy',       loc_nl: 'Italië',              loc_en: 'Italy',       loc_fr: 'Italie',      currency_code: 'EUR', phone_code: '+39',  prefered: 0, shipping_costs: 12.95,shipping_free_from: 125, languages: 'it',    visible: 1 },
];

const MOCK_SEASONS = [
    { id: 'S26', name: 'SS26',     loc_nl: 'Lente / Zomer 2026',   loc_en: 'Spring / Summer 2026',    loc_fr: 'Printemps / Été 2026',       explore_order_priority: 10,    visible: 1, updated_at: '2026-01-15 09:00' },
    { id: 'W25', name: 'FW25',     loc_nl: 'Herfst / Winter 2025', loc_en: 'Fall / Winter 2025',      loc_fr: 'Automne / Hiver 2025',       explore_order_priority: 20,    visible: 1, updated_at: '2025-08-01 09:00' },
    { id: 'S25', name: 'SS25',     loc_nl: 'Lente / Zomer 2025',   loc_en: 'Spring / Summer 2025',    loc_fr: 'Printemps / Été 2025',       explore_order_priority: 30,    visible: 0, updated_at: '2025-01-15 09:00' },
    { id: 'W24', name: 'FW24',     loc_nl: 'Herfst / Winter 2024', loc_en: 'Fall / Winter 2024',      loc_fr: 'Automne / Hiver 2024',       explore_order_priority: 40,    visible: 0, updated_at: '2024-08-01 09:00' },
    { id: 'ALL', name: 'All',      loc_nl: 'Alle seizoenen',       loc_en: 'All seasons',             loc_fr: 'Toutes les saisons',         explore_order_priority: 99999, visible: 1, updated_at: '2024-01-01 09:00' },
];

const MOCK_SIZES = [
    { id: 1, size_x_description: 'XS', size_y_description: '',    group: 10, order: 10, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 2, size_x_description: 'S',  size_y_description: '',    group: 10, order: 20, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 3, size_x_description: 'M',  size_y_description: '',    group: 10, order: 30, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 4, size_x_description: 'L',  size_y_description: '',    group: 10, order: 40, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 5, size_x_description: 'XL', size_y_description: '',    group: 10, order: 50, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 6, size_x_description: 'XXL',size_y_description: '',    group: 10, order: 60, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 7, size_x_description: '38', size_y_description: '',    group: 20, order: 10, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 8, size_x_description: '40', size_y_description: '',    group: 20, order: 20, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 9, size_x_description: '42', size_y_description: '',    group: 20, order: 30, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 10, size_x_description: '44',size_y_description: '',    group: 20, order: 40, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 11, size_x_description: '32',size_y_description: '30',  group: 30, order: 10, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 12, size_x_description: '34',size_y_description: '30',  group: 30, order: 20, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 13, size_x_description: '34',size_y_description: '32',  group: 30, order: 30, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 14, size_x_description: '36',size_y_description: '32',  group: 30, order: 40, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 15, size_x_description: '38',size_y_description: '34',  group: 30, order: 50, active: 1, updated_at: '2025-11-10 10:00' },
];

const SIZE_GROUPS = {
    10: 'Clothing (alpha)',
    20: 'Shoes (EU)',
    30: 'Pants (waist × length)',
    99999: 'Ungrouped',
};

// log_crons based — this is what actually gets written by background sync jobs
const MOCK_SYNC_HISTORY = [
    { id: 3303843, name: 'full_product_sync',    started_on: '2026-04-21 08:15:02', ended_on: '2026-04-21 08:17:10', status: 1, rows_affected: 1245 },
    { id: 3303822, name: 'image_sync',           started_on: '2026-04-21 06:00:01', ended_on: '2026-04-21 06:00:43', status: 1, rows_affected: 89 },
    { id: 3303801, name: 'reservation_sync',     started_on: '2026-04-20 22:00:00', ended_on: '2026-04-20 22:01:07', status: 1, rows_affected: 153 },
    { id: 3303780, name: 'brand_logo_sync',      started_on: '2026-04-20 14:30:00', ended_on: '2026-04-20 14:30:18', status: 1, rows_affected: 12 },
    { id: 3303759, name: 'full_product_sync',    started_on: '2026-04-20 08:15:00', ended_on: '2026-04-20 08:15:05', status: 0, rows_affected: 0 },
    { id: 3303738, name: 'reservation_sync',     started_on: '2026-04-19 22:00:01', ended_on: '2026-04-19 22:01:26', status: 1, rows_affected: 203 },
    { id: 3303717, name: 'attribute_sync',       started_on: '2026-04-19 08:15:02', ended_on: '2026-04-19 08:15:24', status: 1, rows_affected: 45 },
    { id: 3303696, name: 'reservation_sync',     started_on: '2026-04-18 22:00:00', ended_on: '2026-04-18 22:01:11', status: 1, rows_affected: 178 },
    { id: 3303675, name: 'full_product_sync',    started_on: '2026-04-18 08:15:00', ended_on: '2026-04-18 08:17:05', status: 1, rows_affected: 1240 },
    { id: 3303654, name: 'reservation_sync',     started_on: '2026-04-17 22:00:00', ended_on: '2026-04-17 22:00:58', status: 1, rows_affected: 134 },
    { id: 3303633, name: 'image_sync',           started_on: '2026-04-17 06:00:00', ended_on: '2026-04-17 06:00:52', status: 1, rows_affected: 203 },
    { id: 3303612, name: 'full_product_sync',    started_on: '2026-04-17 08:15:00', ended_on: '2026-04-17 08:17:20', status: 1, rows_affected: 1238 },
];

const MOCK_TRANSLATIONS = [
    { id: 1, key: 'add_to_cart',          nl: 'Toevoegen aan winkelmand',                  en: 'Add to cart',                    fr: 'Ajouter au panier' },
    { id: 2, key: 'checkout',             nl: 'Afrekenen',                                 en: 'Checkout',                       fr: 'Paiement' },
    { id: 3, key: 'total',                nl: 'Totaal',                                    en: 'Total',                          fr: 'Total' },
    { id: 4, key: 'shipping',             nl: 'Verzending',                                en: 'Shipping',                       fr: 'Livraison' },
    { id: 5, key: 'free_shipping_from',   nl: 'Gratis verzending vanaf €{amount}',         en: 'Free shipping from €{amount}',   fr: 'Livraison gratuite à partir de €{amount}' },
    { id: 6, key: 'out_of_stock',         nl: 'Uitverkocht',                               en: 'Out of stock',                   fr: 'Rupture de stock' },
    { id: 7, key: 'more_info',            nl: 'Meer info',                                 en: 'More info',                      fr: "Plus d'infos" },
    { id: 8, key: 'contact_us',           nl: 'Neem contact op',                           en: 'Contact us',                     fr: 'Contactez-nous' },
    { id: 9, key: 'size_guide',           nl: 'Maattabel',                                 en: 'Size guide',                     fr: 'Guide des tailles' },
    { id: 10, key: 'return_policy',       nl: 'Retourbeleid',                              en: 'Return policy',                  fr: 'Politique de retour' },
];

// ============================================================
// Helpers
// ============================================================

// Defensive: MySQL → PDO can return tinyint(1) as either a number, a string,
// or a boolean depending on driver flags. Treat anything that round-trips to
// the integer 1 as truthy; everything else (including "0", null, undefined)
// as falsy. Labels go through i18n directly (module scope has no `t` prop).
const boolYesNo = (p) => Number(p.value) === 1
    ? i18n.t('cfg_data_yes', 'Ja')
    : i18n.t('cfg_data_no', 'Nee');
const orNull = (p) => (p.value === null || p.value === undefined || p.value === '') ? '—' : p.value;

// Multiline cell editor — a Popper anchored to the cell with a resizable
// textarea, so long values (translations, names) can be edited comfortably.
// Enter inserts a newline; Ctrl+Enter or clicking away commits; Escape reverts.
const MultilineEditCell = (props) => {
    const { id, field, value, colDef } = props;
    const [valueState, setValueState] = useState(value ?? '');
    const [anchorEl, setAnchorEl] = useState(null);
    const apiRef = useGridApiContext();
    const handleChange = (e) => {
        const newValue = e.target.value;
        setValueState(newValue);
        apiRef.current.setEditCellValue({ id, field, value: newValue, debounceMs: 60 }, e);
    };
    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.ctrlKey) {
            // Keep the newline inside the textarea instead of committing.
            e.stopPropagation();
        } else if (e.key === 'Enter' && e.ctrlKey) {
            apiRef.current.stopCellEditMode({ id, field });
        }
    };
    return (
        <Box sx={{ position: 'relative', alignSelf: 'flex-start', width: '100%' }}>
            <Box ref={setAnchorEl} sx={{ height: 1, width: colDef.computedWidth, display: 'block', position: 'absolute', top: 0 }} />
            {anchorEl && (
                <Popper open anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
                    <Paper elevation={3} sx={{ p: 1, minWidth: colDef.computedWidth, maxWidth: 560 }}>
                        <InputBase
                            multiline
                            minRows={3}
                            value={valueState}
                            onChange={handleChange}
                            onKeyDown={onKeyDown}
                            autoFocus
                            sx={{ width: '100%', fontSize: '0.875rem', '& textarea': { resize: 'both' } }}
                        />
                    </Paper>
                </Popper>
            )}
        </Box>
    );
};

// Add `editable: true` (and infer the right MUI grid type) to every column
// except the ones listed in `nonEditable`. Type inference is intentionally
// narrow:
//   - renderCell === boolYesNo  → type: 'boolean'  (checkbox editor)
//   - align === 'right'         → type: 'number'   (numeric input)
//   - everything else           → string, edited via the multiline popper
// This means the column-builders below stay declarative; we just flip a flag.
const makeEditable = (cols, nonEditable = ['id', 'updated_at']) =>
    cols.map(c => {
        if (nonEditable.includes(c.field)) return c;
        let type;
        if (c.renderCell === boolYesNo) type = 'boolean';
        else if (c.align === 'right')   type = 'number';
        return {
            ...c,
            editable: true,
            ...(type ? { type } : { renderEditCell: (params) => <MultilineEditCell {...params} /> }),
        };
    });

const durationBetween = (start, end) => {
    try {
        const s = new Date(start.replace(' ', 'T'));
        const e = new Date(end.replace(' ', 'T'));
        const diff = Math.round((e - s) / 1000);
        if (diff < 60) return `${diff}s`;
        const m = Math.floor(diff / 60);
        const sec = diff % 60;
        return `${m}m ${sec}s`;
    } catch { return '—'; }
};

// ============================================================

const WebshopDataScreen = (props) => {
    useEffect(() => {
        if (props.changeScreen) props.changeScreen('webshop');
    }, []);

    const tr = (key, fb) => (props.t ? props.t(key, fb) : fb);

    const [tabValue, setTabValue] = useState(0);
    const [syncSearch, setSyncSearch] = useState('');
    const [syncStatus, setSyncStatus] = useState('all');
    const [syncType, setSyncType] = useState('all');

    // Per-tab fetched-data cache. Key = tab index. Empty object on first
    // render — gets reset whenever the user picks a different webshop.
    const [tabData, setTabData] = useState({});
    const [tabLoading, setTabLoading] = useState({});
    const [tabError, setTabError] = useState({});
    // Buffered cell edits — NOTHING is PATCHed on cell commit anymore (too
    // error-prone); edits collect here per tab/row/column until the user
    // presses Save (or discards). Shape: { [tabIdx]: { [rowId]: { col: val } } }.
    const [pendingEdits, setPendingEdits] = useState({});
    const pendingForTab   = pendingEdits[tabValue] || {};
    const pendingRowCount = Object.keys(pendingForTab).length;
    const [savingEdits, setSavingEdits] = useState(false);
    // Unsaved-changes guard — buffered edits across ALL tables count; the
    // sidebar/webshop-picker/browser-close consult this before discarding.
    // The ref holds per-table labels (filled in below, after TAB_ENDPOINTS
    // exists) so the confirmation dialog can list what would be lost.
    const pendingItemsRef = useRef([]);
    // Live ref to the save-all fn (defined below) so the guard's "Save &
    // continue" calls the current closure.
    const saveAllRef = useRef(null);
    useEffect(() => {
        registerUnsavedCheck(
            'webshop-data',
            () => pendingItemsRef.current,
            () => saveAllRef.current && saveAllRef.current(),
        );
        return () => unregisterUnsavedCheck('webshop-data');
    }, []);
    // Bulk edit — checkbox-select rows, pick a column + value, apply to all
    // selected rows at once (lands in the same pendingEdits buffer).
    const [rowSelection, setRowSelection] = useState(null);
    const [bulkField, setBulkField] = useState('');
    const [bulkValue, setBulkValue] = useState('');
    useEffect(() => {
        // Selection/bulk inputs are tab-specific — reset on tab switch (the
        // grid itself remounts via key={tabValue}, clearing its checkboxes).
        setRowSelection(null);
        setBulkField('');
        setBulkValue('');
    }, [tabValue]);
    // v7 gives an array of ids; v8 gives { type: 'include'|'exclude', ids: Set }.
    const selectedIdsFrom = (model, allRows) => {
        if (!model) return [];
        if (Array.isArray(model)) return model;
        const ids = (model.ids instanceof Set || Array.isArray(model.ids)) ? Array.from(model.ids) : [];
        if (model.type === 'exclude') {
            const excluded = new Set(ids.map(String));
            return (allRows || []).map(r => r.id).filter(id => !excluded.has(String(id)));
        }
        return ids;
    };

    const isDark = props.theme === 'dark';
    const cardBg = isDark ? '#2d333b' : '#ffffff';
    const borderColor = isDark ? '#3a3f47' : '#e4e7eb';
    const textColor = isDark ? Colors.dark_text : Colors.light_text;
    const mutedText = isDark ? '#b0b8c1' : '#768390';

    // ============================================================
    // Column definitions
    // ============================================================

    // Shared column-header labels (re-used across the tables below).
    const H = {
        name:     tr('cfg_data_col_name',     'Naam'),
        visible:  tr('cfg_data_col_visible',  'Zichtbaar'),
        updated:  tr('cfg_data_col_updated',  'Bijgewerkt'),
        order:    tr('cfg_data_col_order',    'Volgorde'),
        priority: tr('cfg_data_col_priority', 'Prioriteit'),
    };

    const BRAND_COLUMNS = [
        { field: 'id',                          headerName: 'ID',          width: 75,  display: 'flex' },
        { field: 'name',                        headerName: H.name,        flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'slug',                        headerName: 'Slug',        flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'display_name',                headerName: tr('cfg_data_col_display', 'Weergavenaam'), flex: 1, minWidth: 140, display: 'flex' },
        { field: 'external_supplier',           headerName: tr('cfg_data_col_ext_supplier', 'Ext. leverancier'), width: 130, display: 'flex', renderCell: orNull },
        { field: 'use_external_stock',          headerName: tr('cfg_data_col_ext_stock', 'Ext. voorraad'), width: 115, display: 'flex', renderCell: boolYesNo },
        { field: 'fetch_not_in_stock_products', headerName: tr('cfg_data_col_fetch_oos', 'Zonder voorraad ophalen'), width: 115, display: 'flex', renderCell: boolYesNo },
        { field: 'explore_order_priority',      headerName: H.priority,    width: 110, align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'visible',                     headerName: H.visible,     width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'logo_visible',                headerName: 'Logo',        width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'updated_at',                  headerName: H.updated,     width: 170, display: 'flex' },
    ];

    const CATEGORY_COLUMNS = [
        { field: 'id',                     headerName: 'ID',       width: 80,  display: 'flex' },
        { field: 'level',                  headerName: tr('cfg_data_col_level', 'Niveau'), width: 80, align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'name',                   headerName: H.name,     flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_nl',                 headerName: 'NL',       flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_en',                 headerName: 'EN',       flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_fr',                 headerName: 'FR',       flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'order_index',            headerName: H.order,    width: 95,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'explore_order_priority', headerName: H.priority, width: 105, align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'visible',                headerName: H.visible,  width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'updated_at',             headerName: H.updated,  width: 170, display: 'flex' },
    ];

    const COLOR_COLUMNS = [
        { field: 'id',       headerName: 'ID',    width: 80,  display: 'flex' },
        { field: 'name',     headerName: H.name,  flex: 1,    minWidth: 120, display: 'flex' },
        {
            field: 'hex',
            headerName: tr('cfg_data_col_swatch', 'Kleurstaal'),
            width: 150,
            display: 'flex',
            renderCell: (p) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                        width: 22,
                        height: 22,
                        borderRadius: 0.75,
                        border: '1px solid rgba(0,0,0,0.15)',
                        backgroundColor: p.value,
                        flexShrink: 0,
                    }} />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value}</Typography>
                </Box>
            ),
        },
        { field: 'loc_nl',  headerName: 'NL',      flex: 1, minWidth: 110, display: 'flex' },
        { field: 'loc_en',  headerName: 'EN',      flex: 1, minWidth: 110, display: 'flex' },
        { field: 'loc_fr',  headerName: 'FR',      flex: 1, minWidth: 110, display: 'flex' },
        { field: 'visible', headerName: H.visible, width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'updated_at', headerName: H.updated, width: 170, display: 'flex' },
    ];

    const COUNTRY_COLUMNS = [
        { field: 'id',                 headerName: 'ID',            width: 70,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'iso',                headerName: 'ISO',           width: 75,  display: 'flex' },
        { field: 'iso_3',              headerName: 'ISO-3',         width: 85,  display: 'flex' },
        { field: 'name',               headerName: H.name,          flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_nl',             headerName: 'NL',            flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_en',             headerName: 'EN',            flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_fr',             headerName: 'FR',            flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'currency_code',      headerName: tr('cfg_data_col_currency', 'Munt'),        width: 100, display: 'flex' },
        { field: 'phone_code',         headerName: tr('cfg_data_col_phone_code', 'Landcode'),  width: 110, display: 'flex' },
        { field: 'prefered',           headerName: tr('cfg_data_col_preferred', 'Voorkeur'),   width: 115, display: 'flex', renderCell: boolYesNo },
        { field: 'shipping_costs',     headerName: tr('cfg_data_col_shipping', 'Verzending'),  width: 120, align: 'right', headerAlign: 'right', display: 'flex', renderCell: (p) => `€${Number(p.value).toFixed(2)}` },
        { field: 'shipping_free_from', headerName: tr('cfg_data_col_free_from', 'Gratis vanaf'), width: 120, align: 'right', headerAlign: 'right', display: 'flex', renderCell: (p) => `€${Number(p.value).toFixed(2)}` },
        { field: 'languages',          headerName: tr('cfg_data_col_languages', 'Talen'),      width: 130, display: 'flex' },
        { field: 'visible',            headerName: H.visible,       width: 100, display: 'flex', renderCell: boolYesNo },
    ];

    const SEASON_COLUMNS = [
        { field: 'id',                     headerName: 'ID',       width: 80,  display: 'flex' },
        { field: 'name',                   headerName: tr('cfg_data_col_code', 'Code'), width: 120, display: 'flex' },
        { field: 'loc_nl',                 headerName: 'NL',       flex: 1,    minWidth: 180, display: 'flex' },
        { field: 'loc_en',                 headerName: 'EN',       flex: 1,    minWidth: 180, display: 'flex' },
        { field: 'loc_fr',                 headerName: 'FR',       flex: 1,    minWidth: 180, display: 'flex' },
        { field: 'explore_order_priority', headerName: H.priority, width: 105, align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'visible',                headerName: H.visible,  width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'updated_at',             headerName: H.updated,  width: 170, display: 'flex' },
    ];

    const SIZE_COLUMNS = [
        { field: 'id',                 headerName: 'ID',     width: 90,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'size_x_description', headerName: tr('cfg_data_col_size_x', 'Maat X'), width: 120, display: 'flex' },
        { field: 'size_y_description', headerName: tr('cfg_data_col_size_y', 'Maat Y'), width: 120, display: 'flex', renderCell: (p) => p.value || '—' },
        { field: 'group',              headerName: tr('cfg_data_col_group', 'Groep'),   flex: 1, minWidth: 200, display: 'flex', renderCell: (p) => SIZE_GROUPS[p.value] || `${tr('cfg_data_col_group', 'Groep')} ${p.value}` },
        { field: 'order',              headerName: H.order,  width: 90,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'active',             headerName: tr('cfg_data_col_active', 'Actief'), width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'updated_at',         headerName: H.updated, width: 170, display: 'flex' },
    ];

    // Reservations / receipts. Read-only by design — these are financial
    // records and shouldn't be patched via a grid edit.
    const RESERVATION_COLUMNS = [
        { field: 'id',                 headerName: 'ID',          width: 75,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'reservation_id',     headerName: tr('cfg_data_col_order_id', 'Bestelnr.'), width: 130, display: 'flex' },
        {
            field: 'reservation_type',
            headerName: 'Type',
            width: 130,
            display: 'flex',
            renderCell: (p) => {
                const isReceipt = p.value === 'RECEIPT';
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            backgroundColor: isReceipt ? Colors.sft_blue : '#9c27b0',
                        }} />
                        <Typography variant="body2">{p.value || '—'}</Typography>
                    </Box>
                );
            },
        },
        { field: 'delivery_type',      headerName: tr('cfg_data_col_delivery', 'Levering'), width: 110, display: 'flex', renderCell: (p) => p.value || '—' },
        {
            field: 'total_amount',
            headerName: tr('cfg_data_col_total', 'Totaal'),
            width: 110,
            align: 'right',
            headerAlign: 'right',
            display: 'flex',
            renderCell: (p) => p.value != null ? `€${Number(p.value).toFixed(2)}` : '—',
        },
        {
            field: 'customer_name',
            headerName: tr('cfg_data_col_customer', 'Klant'),
            flex: 1, minWidth: 180,
            display: 'flex',
            // Synthesized client-side from the firstname + lastname columns the
            // mini-api extracts out of customer_data, so the grid stays sortable
            // and searchable by full name.
            valueGetter: (_v, row) => {
                const parts = [row.customer_firstname, row.customer_lastname].filter(Boolean);
                return parts.join(' ').trim() || '—';
            },
        },
        { field: 'customer_email',     headerName: tr('cfg_data_col_email', 'E-mail'), flex: 1, minWidth: 200, display: 'flex', renderCell: (p) => p.value || '—' },
        { field: 'store_id',           headerName: tr('cfg_data_col_store', 'Winkel'), width: 80, align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'language',           headerName: tr('cfg_data_col_lang', 'Taal'),    width: 75,  display: 'flex' },
        { field: 'payed_status',       headerName: tr('cfg_data_col_payed', 'Betaald'), width: 120, display: 'flex', renderCell: (p) => p.value || '—' },
        { field: 'payed_via',          headerName: tr('cfg_data_col_payed_via', 'Via'), width: 90, display: 'flex', renderCell: (p) => p.value || '—' },
        { field: 'sent_email',         headerName: tr('cfg_data_col_email_sent', 'E-mail verzonden'), width: 110, display: 'flex', renderCell: boolYesNo },
        { field: 'sent_reservation',   headerName: tr('cfg_data_col_sent', 'Verzonden'), width: 90, display: 'flex', renderCell: boolYesNo },
        { field: 'is_queued',          headerName: tr('cfg_data_col_queued', 'In wachtrij'), width: 90, display: 'flex', renderCell: boolYesNo },
        { field: 'created_at',         headerName: tr('cfg_data_col_created', 'Aangemaakt'), width: 170, display: 'flex' },
    ];

    const TRANSLATION_COLUMNS = [
        { field: 'id',  headerName: 'ID',  width: 70,   display: 'flex' },
        { field: 'key', headerName: tr('cfg_data_col_key', 'Sleutel'), flex: 1, minWidth: 200, display: 'flex', renderCell: (p) => (
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value}</Typography>
        ) },
        { field: 'nl',  headerName: 'NL',  flex: 1.5,   minWidth: 200, display: 'flex' },
        { field: 'en',  headerName: 'EN',  flex: 1.5,   minWidth: 200, display: 'flex' },
        { field: 'fr',  headerName: 'FR',  flex: 1.5,   minWidth: 200, display: 'flex' },
    ];

    const SYNC_COLUMNS = [
        { field: 'id',         headerName: 'ID',       width: 100, align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'name',       headerName: tr('cfg_data_col_job', 'Taak'), flex: 1, minWidth: 200, display: 'flex', renderCell: (p) => (
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value}</Typography>
        ) },
        { field: 'started_on', headerName: tr('cfg_data_col_started', 'Gestart'),  width: 180, display: 'flex' },
        { field: 'ended_on',   headerName: tr('cfg_data_col_ended',   'Beëindigd'), width: 180, display: 'flex' },
        {
            field: 'duration',
            headerName: tr('cfg_data_col_duration', 'Duur'),
            width: 110,
            align: 'right',
            headerAlign: 'right',
            display: 'flex',
            sortable: false,
            renderCell: (p) => durationBetween(p.row.started_on, p.row.ended_on),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            display: 'flex',
            renderCell: (p) => {
                const success = p.value === 1;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: success ? '#4caf50' : Colors.def_red }} />
                        <Typography variant="body2">{success ? tr('cfg_data_sync_success', 'Geslaagd') : tr('cfg_data_sync_failed', 'Mislukt')}</Typography>
                    </Box>
                );
            },
        },
        { field: 'rows_affected', headerName: tr('cfg_data_col_rows', 'Rijen'), width: 110, align: 'right', headerAlign: 'right', display: 'flex' },
    ];

    // ============================================================
    // Sync filter logic — applied client-side to whatever rows the mini-api
    // returns for the Sync history tab.
    // ============================================================

    const filteredSyncHistory = useCallback((rows) => {
        const q = syncSearch.trim().toLowerCase();
        return (rows || []).filter(row => {
            if (syncStatus === 'success' && row.status != 1) return false;
            if (syncStatus === 'failed'  && row.status == 1) return false;
            if (syncType !== 'all' && row.name !== syncType) return false;
            if (q) {
                const haystack = `${row.name ?? ''} ${row.started_on ?? ''} ${row.ended_on ?? ''} ${row.id ?? ''}`.toLowerCase();
                if (!haystack.includes(q)) return false;
            }
            return true;
        });
    }, [syncSearch, syncStatus, syncType]);

    // ============================================================
    // Tab → mini-api endpoint mapping. Each entry knows which path on the
    // webshop's mini-api to GET, and (optionally) how to massage the
    // response into the row shape the DataGrid expects. `editable` controls
    // inline cell editing — sync history is a log so it stays read-only;
    // everything else PATCHes via `${path}/{id}` (overridable via patchPath).
    // ============================================================
    // `name` is the POS-sync match key on every attribute table — the old
    // admin never allows editing it (only the loc_* translations and flags),
    // so it stays read-only here too.
    const TAB_ENDPOINTS = useMemo(() => ([
        { label: tr('cfg_data_tab_brands', 'Merken'),     columns: makeEditable(BRAND_COLUMNS, ['id', 'updated_at', 'name']),     path: '/brands',     editable: true },
        { label: tr('cfg_data_tab_countries', 'Landen'),  columns: makeEditable(COUNTRY_COLUMNS, ['id', 'updated_at', 'name']),   path: '/countries',  editable: true },
        // `level` is structural for categories — flagging it editable would let admins
        // break the tree silently since the mini-api PATCH whitelist excludes it.
        { label: tr('cfg_data_tab_categories', 'Categorieën'), columns: makeEditable(CATEGORY_COLUMNS, ['id', 'updated_at', 'level', 'name']), path: '/categories', editable: true },
        { label: tr('cfg_data_tab_colors', 'Kleuren'),     columns: makeEditable(COLOR_COLUMNS, ['id', 'updated_at', 'name']),     path: '/colors',     editable: true },
        // Seasons' `name` is the POS season code — same structural rule.
        { label: tr('cfg_data_tab_seasons', 'Seizoenen'),  columns: makeEditable(SEASON_COLUMNS, ['id', 'updated_at', 'name']),    path: '/seasons',    editable: true },
        { label: tr('cfg_data_tab_sizes', 'Maten'),        columns: makeEditable(SIZE_COLUMNS),                                    path: '/sizes',      editable: true },
        { label: tr('cfg_data_tab_reservations', 'Reservaties'), columns: RESERVATION_COLUMNS, path: '/reservations', editable: false },
        { label: tr('cfg_data_tab_sync', 'Sync-historiek'), columns: SYNC_COLUMNS,        path: '/admin/log',
          transform: (data) => (data?.crons || []).map((r, i) => ({ id: r.id ?? i, ...r })),
          isSync: true,
          editable: false },
        { label: tr('cfg_data_tab_translations', 'Vertalingen'), columns: makeEditable(TRANSLATION_COLUMNS),
          path: '/localization',
          editable: true,
          // i18n stores the lookup token as `item`; the grid uses `key`. The GET
          // transform maps item→key for display; bodyTransform reverses it on PATCH.
          transform: (rows) => (rows || []).map(r => ({ ...r, key: r.item ?? r.key })),
          bodyTransform: (changes) => {
              const out = { ...changes };
              if ('key' in out) { out.item = out.key; delete out.key; }
              return out;
          },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ]), [filteredSyncHistory]);

    // Per-table unsaved-edit labels for the global guard (needs the
    // translated TAB_ENDPOINTS labels, hence after that definition).
    pendingItemsRef.current = Object.entries(pendingEdits)
        .filter(([, rows]) => Object.keys(rows || {}).length > 0)
        .map(([idx, rows]) => ({
            icon: 'data',
            tab: tr('cfg_data_title', 'Webshop data'),
            section: `${TAB_ENDPOINTS[idx]?.label || idx} (${Object.keys(rows).length})`,
        }));

    // Find the Sync-history tab dynamically so this lookup survives tab
    // reorders or insertions.
    const syncTabIndex = useMemo(
        () => TAB_ENDPOINTS.findIndex(t => t.isSync),
        [TAB_ENDPOINTS]
    );
    const syncTypes = useMemo(() => {
        const rows = (syncTabIndex >= 0 ? tabData[syncTabIndex] : null) || [];
        const types = new Set(rows.map(r => r.name).filter(Boolean));
        return ['all', ...Array.from(types)];
    }, [tabData, syncTabIndex]);

    // Reset cache whenever the user switches webshops — including any
    // unsaved buffered edits (they belong to the previous webshop).
    useEffect(() => {
        setTabData({});
        setTabError({});
        setPendingEdits({});
    }, [props.selectedWebshop?.id]);

    // Fetch the data for a tab, lazily, and cache it.
    const fetchTab = useCallback(async (idx) => {
        const def = TAB_ENDPOINTS[idx];
        if (!def) return;
        const webshopId = props.selectedWebshop?.id;
        if (!webshopId || !props.api) return;
        setTabLoading(prev => ({ ...prev, [idx]: true }));
        setTabError(prev => ({ ...prev, [idx]: null }));
        try {
            const result = await SftGlobalData.post('webshop&action=proxy', {
                key:        props.api,
                webshop_id: webshopId,
                sftUserId:  props.login?.sft_user_id,
                user:       props.login?.login,
                method:     'GET',
                path:       def.path,
            });
            if (result && result.success) {
                const transformed = def.transform ? def.transform(result.data) : (result.data || []);
                setTabData(prev => ({ ...prev, [idx]: Array.isArray(transformed) ? transformed : [] }));
            } else {
                setTabError(prev => ({ ...prev, [idx]: { message: result?.message || 'Failed to load', code: result?.translation_key } }));
                setTabData(prev => ({ ...prev, [idx]: [] }));
            }
        } catch (e) {
            setTabError(prev => ({ ...prev, [idx]: { message: e?.message || 'Network error', code: 'error_network' } }));
            setTabData(prev => ({ ...prev, [idx]: [] }));
        } finally {
            setTabLoading(prev => ({ ...prev, [idx]: false }));
        }
    }, [TAB_ENDPOINTS, props.api, props.login?.sft_user_id, props.login?.login, props.selectedWebshop?.id]);

    // When the tab or webshop changes, fetch lazily if not already cached.
    useEffect(() => {
        if (tabData[tabValue] === undefined && !tabLoading[tabValue]) {
            fetchTab(tabValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, props.selectedWebshop?.id, fetchTab]);

    // Country preset (bulk-apply visible/prefered to all countries). Direct,
    // destructive action via POST /countries/default-values, then refresh the grid.
    const [countryPreset, setCountryPreset] = useState('default');
    const [presetBusy, setPresetBusy] = useState(false);
    const applyCountryPreset = useCallback(async () => {
        const webshopId = props.selectedWebshop?.id;
        if (!webshopId || !props.api) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm(tr('cfg_proc_ship_country_preset_confirm', 'Dit reset de zichtbaarheid + voorkeur-vlag van ALLE landen naar de gekozen voorinstelling. Verzendtarieven per land blijven ongewijzigd. Doorgaan?'))) return;
        setPresetBusy(true);
        try {
            const r = await SftGlobalData.post('webshop&action=proxy', {
                key:        props.api,
                webshop_id: webshopId,
                sftUserId:  props.login?.sft_user_id,
                user:       props.login?.login,
                method:     'POST',
                path:       '/countries/default-values',
                body:       { template: countryPreset },
            });
            if (r?.success && !(r.data && r.data.status === false)) {
                setTabData(prev => { const n = { ...prev }; delete n[tabValue]; return n; });
                fetchTab(tabValue);
            }
        } catch { /* swallow — leave the grid as-is */ }
        setPresetBusy(false);
    }, [props.api, props.selectedWebshop?.id, props.login?.sft_user_id, props.login?.login, countryPreset, tabValue, fetchTab]);

    // Inline cell-edit commit handler. DataGridPro fires this after the user
    // commits an edit (Enter / blur). Edits are BUFFERED (not PATCHed) —
    // direct-on-edit saving proved too error-prone, so changes collect in
    // `pendingEdits` and only hit the server when the user presses Save.
    const processRowUpdate = useCallback((newRow, oldRow) => {
        const def = TAB_ENDPOINTS[tabValue];
        if (!def || def.editable === false) return oldRow;

        // Diff the two rows. id is excluded as a safety net (column is
        // non-editable anyway, but make the contract explicit).
        const changes = {};
        for (const key of Object.keys(newRow)) {
            if (key === 'id') continue;
            if (newRow[key] !== oldRow[key]) changes[key] = newRow[key];
        }
        if (Object.keys(changes).length === 0) return oldRow;

        setPendingEdits(prev => ({
            ...prev,
            [tabValue]: {
                ...(prev[tabValue] || {}),
                [newRow.id]: { ...((prev[tabValue] || {})[newRow.id] || {}), ...changes },
            },
        }));
        // Show the edited value in the grid right away (server state stays
        // untouched until Save; Discard refetches the tab).
        setTabData(prev => {
            const list = prev[tabValue] || [];
            return {
                ...prev,
                [tabValue]: list.map(r => r.id === newRow.id ? newRow : r),
            };
        });
        return newRow;
    }, [TAB_ENDPOINTS, tabValue]);

    const handleProcessRowUpdateError = useCallback((error) => {
        if (props.notify) props.notify(tr('cfg_data_edit_failed', 'Bewerking mislukt: ') + (error?.message || ''), 5000, 'top-right', props.theme, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.notify, props.theme]);

    // Persist the buffered edits for ONE tab — one PATCH per changed row with
    // only the changed columns. Returns the count of rows that failed (0 = ok).
    // `silent` suppresses the per-tab notify (used by the save-all path).
    const saveTabEditsFor = useCallback(async (idx, { silent } = {}) => {
        const def = TAB_ENDPOINTS[idx];
        const webshopId = props.selectedWebshop?.id;
        const edits = pendingEdits[idx] || {};
        const rowIds = Object.keys(edits);
        if (!def || !webshopId || !props.api || rowIds.length === 0) return 0;
        const failed = [];
        for (const rowId of rowIds) {
            const changes = edits[rowId];
            const row = (tabData[idx] || []).find(r => String(r.id) === String(rowId)) || { id: rowId };
            const body = def.bodyTransform ? def.bodyTransform({ ...changes }, row) : changes;
            const patchPath = (def.patchPath ? def.patchPath(rowId) : `${def.path}/${rowId}`);
            try {
                const result = await SftGlobalData.post('webshop&action=proxy', {
                    key:        props.api,
                    webshop_id: webshopId,
                    sftUserId:  props.login?.sft_user_id,
                    user:       props.login?.login,
                    method:     'PATCH',
                    path:       patchPath,
                    body,
                });
                if (!result || !result.success) failed.push(rowId);
            } catch {
                failed.push(rowId);
            }
        }
        if (failed.length) {
            // Keep ONLY the failed rows pending so a retry is targeted.
            setPendingEdits(prev => ({
                ...prev,
                [idx]: Object.fromEntries(Object.entries(prev[idx] || {}).filter(([id]) => failed.includes(id))),
            }));
            if (!silent && props.notify) props.notify(`${tr('cfg_data_save_partial', 'Niet alles opgeslagen — mislukte rijen:')} ${failed.join(', ')}`, 6000, 'top-right', props.theme, 1);
        } else {
            setPendingEdits(prev => ({ ...prev, [idx]: {} }));
            if (!silent && props.notify) props.notify(tr('cfg_data_saved', 'Wijzigingen opgeslagen'), 3000, 'top-right', props.theme, 2);
        }
        return failed.length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [TAB_ENDPOINTS, pendingEdits, tabData, props.api,
        props.login?.sft_user_id, props.login?.login,
        props.selectedWebshop?.id, props.notify, props.theme]);

    // Active-tab save (the yellow bar's button).
    const saveTabEdits = useCallback(async () => {
        setSavingEdits(true);
        await saveTabEditsFor(tabValue);
        setSavingEdits(false);
    }, [saveTabEditsFor, tabValue]);

    // Save EVERY tab that has buffered edits — used by the global unsaved-
    // changes guard's "Save & continue". One summary notify at the end.
    const saveAllTabEdits = useCallback(async () => {
        const dirtyTabs = Object.keys(pendingEdits).filter(idx => Object.keys(pendingEdits[idx] || {}).length > 0);
        if (dirtyTabs.length === 0) return;
        setSavingEdits(true);
        let failedTotal = 0;
        for (const idx of dirtyTabs) {
            failedTotal += await saveTabEditsFor(Number(idx), { silent: true });
        }
        setSavingEdits(false);
        if (props.notify) {
            if (failedTotal) props.notify(tr('cfg_data_save_partial', 'Niet alles opgeslagen — mislukte rijen:'), 6000, 'top-right', props.theme, 1);
            else props.notify(tr('cfg_data_saved', 'Wijzigingen opgeslagen'), 3000, 'top-right', props.theme, 2);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveTabEditsFor, pendingEdits, props.notify, props.theme]);
    saveAllRef.current = saveAllTabEdits;

    // Discard: drop the buffered edits and refetch the tab so the grid shows
    // the server state again.
    const discardTabEdits = useCallback(() => {
        setPendingEdits(prev => ({ ...prev, [tabValue]: {} }));
        setTabData(prev => { const n = { ...prev }; delete n[tabValue]; return n; });
        fetchTab(tabValue);
    }, [tabValue, fetchTab]);

    const tabs = TAB_ENDPOINTS.map((def, idx) => {
        const fetched = tabData[idx];
        const rows = def.isSync
            ? filteredSyncHistory(fetched || [])
            : (fetched || []);
        return { ...def, rows };
    });

    const currentTab = tabs[tabValue];

    // Bulk edit derivations — selected ids + the editable columns of this tab.
    const selectedIds = selectedIdsFrom(rowSelection, currentTab.rows);
    const bulkColumns = (currentTab.columns || []).filter(c => c.editable && !['id', 'updated_at'].includes(c.field));
    const bulkCol     = bulkColumns.find(c => c.field === bulkField) || null;
    const applyBulkEdit = () => {
        if (!bulkCol || selectedIds.length === 0) return;
        let val = bulkValue;
        if (bulkCol.type === 'boolean')     val = bulkValue === '1' ? 1 : 0;
        else if (bulkCol.type === 'number') val = Number(bulkValue) || 0;
        const rowsById = Object.fromEntries((currentTab.rows || []).map(r => [String(r.id), r]));
        let ids = selectedIds.filter(id => rowsById[String(id)]);
        // BE shipping cells are locked (defined in processing settings) —
        // mirror the isCellEditable guard for bulk writes.
        if (currentTab.path === '/countries' && (bulkField === 'shipping_costs' || bulkField === 'shipping_free_from')) {
            ids = ids.filter(id => rowsById[String(id)]?.iso !== 'BE');
        }
        // Skip rows that already hold the value — keeps the pending buffer honest.
        ids = ids.filter(id => String(rowsById[String(id)][bulkField]) !== String(val));
        if (ids.length === 0) return;
        setPendingEdits(prev => ({
            ...prev,
            [tabValue]: {
                ...(prev[tabValue] || {}),
                ...Object.fromEntries(ids.map(id => [id, { ...((prev[tabValue] || {})[id] || {}), [bulkField]: val }])),
            },
        }));
        setTabData(prev => ({
            ...prev,
            [tabValue]: (prev[tabValue] || []).map(r => ids.some(id => String(id) === String(r.id)) ? { ...r, [bulkField]: val } : r),
        }));
    };

    // No webshop linked → don't show an empty data grid; make it explicit.
    if (!props.selectedWebshop) {
        return (
            <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
                <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, backgroundColor: cardBg, p: 3 }}>
                    <NoWebshopState t={props.t} api={props.api} onProvisioned={props.onProvisioned} notify={props.notify} theme={props.theme} />
                </Box>
            </div>
        );
    }

    // Mini-api unreachable on the active tab → replace the whole table chrome
    // (tabs + headers) with the same error state as the config screen, instead
    // of leaving an empty grid with visible headers.
    if (tabError[tabValue]) {
        return (
            <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
                <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, backgroundColor: cardBg, p: 3 }}>
                    <WebshopErrorState error={tabError[tabValue]} t={props.t} lang={props.lang} />
                </Box>
            </div>
        );
    }

    return (
        <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ color: textColor, fontWeight: 500, mb: 0.5 }}>
                    {tr('cfg_data_title', 'Webshop data')}
                </Typography>
                <Typography variant="body2" sx={{ color: mutedText }}>
                    {props.selectedWebshop
                        ? `${tr('cfg_data_subtitle_live', 'Live gegevens van')} ${props.selectedWebshop.display_name || props.selectedWebshop.slug}.`
                        : tr('cfg_data_subtitle_pick', 'Kies een webshop in de zijbalk om de gegevens te bekijken.')}
                </Typography>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${borderColor}`,
                    borderRadius: 2,
                    backgroundColor: cardBg,
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center' }}>
                    <Tabs
                        value={tabValue}
                        onChange={(_e, v) => setTabValue(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ px: 1, flex: 1 }}
                    >
                        {tabs.map((t) => (
                            <Tab key={t.label} label={t.label} sx={{ textTransform: 'none', fontWeight: 500 }} />
                        ))}
                    </Tabs>
                    <Tooltip title={tr('cfg_data_refresh', 'Vernieuwen')}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setTabData(prev => { const n = { ...prev }; delete n[tabValue]; return n; });
                                    setTabError(prev => ({ ...prev, [tabValue]: null }));
                                    fetchTab(tabValue);
                                }}
                                disabled={!props.selectedWebshop || !!tabLoading[tabValue]}
                                sx={{ mr: 1 }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                <Box sx={{ p: 2 }}>
                    {currentTab.isSync && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                            <TextField
                                size="small"
                                placeholder={tr('cfg_data_sync_search_ph', 'Zoek op taaknaam, datum of ID…')}
                                value={syncSearch}
                                onChange={(e) => setSyncSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: mutedText }} fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ flex: 1, minWidth: 240 }}
                            />
                            <TextField
                                select
                                size="small"
                                label="Status"
                                value={syncStatus}
                                onChange={(e) => setSyncStatus(e.target.value)}
                                sx={{ minWidth: 160 }}
                            >
                                <MenuItem value="all">{tr('cfg_data_sync_all_statuses', 'Alle statussen')}</MenuItem>
                                <MenuItem value="success">{tr('cfg_data_sync_success', 'Geslaagd')}</MenuItem>
                                <MenuItem value="failed">{tr('cfg_data_sync_failed', 'Mislukt')}</MenuItem>
                            </TextField>
                            <TextField
                                select
                                size="small"
                                label={tr('cfg_data_col_job', 'Taak')}
                                value={syncType}
                                onChange={(e) => setSyncType(e.target.value)}
                                sx={{ minWidth: 220 }}
                            >
                                {syncTypes.map(t => (
                                    <MenuItem key={t} value={t}>
                                        {t === 'all' ? tr('cfg_data_sync_all_jobs', 'Alle taken') : t}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    )}

                    {currentTab.path === '/countries' && props.selectedWebshop && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Typography variant="body2" sx={{ color: mutedText }}>
                                {tr('cfg_data_preset_label', 'Standaard landselectie:')}
                            </Typography>
                            <TextField select size="small" value={countryPreset} onChange={(e) => setCountryPreset(e.target.value)} sx={{ minWidth: 240 }}>
                                <MenuItem value="allOff">{tr('cfg_proc_ship_country_preset_all_off', 'Alle landen uitschakelen')}</MenuItem>
                                <MenuItem value="allOn">{tr('cfg_proc_ship_country_preset_all_on', 'Alle landen inschakelen (voorkeur België)')}</MenuItem>
                                <MenuItem value="default">{tr('cfg_proc_ship_country_preset_default', 'Standaard')}</MenuItem>
                                <MenuItem value="belgium">{tr('cfg_proc_ship_country_preset_belgium', 'België')}</MenuItem>
                                <MenuItem value="benelux">{tr('cfg_proc_ship_country_preset_benelux', 'Benelux (geen voorkeur)')}</MenuItem>
                                <MenuItem value="europe">{tr('cfg_proc_ship_country_preset_europe', 'Europa (voorkeur België)')}</MenuItem>
                            </TextField>
                            <Button variant="outlined" size="small" disabled={presetBusy} onClick={applyCountryPreset} sx={{ textTransform: 'none' }}>
                                {presetBusy ? tr('cfg_data_preset_applying', 'Bezig met toepassen…') : tr('cfg_proc_ship_country_preset_apply', 'Toepassen')}
                            </Button>
                            <Typography variant="caption" sx={{ color: mutedText }}>
                                {tr('cfg_data_preset_hint', 'Overschrijft zichtbaarheid/voorkeur voor alle landen; verzendtarieven per land blijven onaangeroerd.')}
                            </Typography>
                        </Box>
                    )}

                    {/* Bulk edit — apply one value to all checkbox-selected
                        rows at once. Lands in the same pending buffer below. */}
                    {currentTab.editable !== false && selectedIds.length >= 2 && (
                        <Box sx={{
                            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2,
                            px: 1.5, py: 1, borderRadius: 1.5,
                            backgroundColor: isDark ? 'rgba(25,118,210,0.08)' : 'rgba(25,118,210,0.06)',
                            border: '1px solid', borderColor: isDark ? 'rgba(25,118,210,0.35)' : 'rgba(25,118,210,0.30)',
                        }}>
                            <Typography variant="body2" sx={{ color: textColor, fontWeight: 600 }}>
                                {selectedIds.length} {tr('cfg_data_bulk_selected', 'rijen geselecteerd')}
                            </Typography>
                            <TextField select size="small" label={tr('cfg_data_bulk_field', 'Kolom')}
                                value={bulkField}
                                onChange={(e) => { setBulkField(e.target.value); setBulkValue(''); }}
                                sx={{ minWidth: 180 }}
                            >
                                {bulkColumns.map(c => (
                                    <MenuItem key={c.field} value={c.field}>{c.headerName || c.field}</MenuItem>
                                ))}
                            </TextField>
                            {bulkCol && bulkCol.type === 'boolean' && (
                                <TextField select size="small" label={tr('cfg_data_bulk_value', 'Waarde')}
                                    value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} sx={{ minWidth: 120 }}>
                                    <MenuItem value="1">{tr('cfg_data_yes', 'Ja')}</MenuItem>
                                    <MenuItem value="0">{tr('cfg_data_no', 'Nee')}</MenuItem>
                                </TextField>
                            )}
                            {bulkCol && bulkCol.type === 'number' && (
                                <TextField size="small" type="number" label={tr('cfg_data_bulk_value', 'Waarde')}
                                    value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} sx={{ width: 140 }} />
                            )}
                            {bulkCol && !bulkCol.type && (
                                <TextField size="small" label={tr('cfg_data_bulk_value', 'Waarde')}
                                    value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} sx={{ minWidth: 220 }} />
                            )}
                            <Button size="small" variant="contained"
                                disabled={!bulkCol || (bulkCol.type !== 'boolean' && bulkValue === '') || (bulkCol.type === 'boolean' && bulkValue === '')}
                                onClick={applyBulkEdit} sx={{ textTransform: 'none' }}>
                                {tr('cfg_data_bulk_apply', 'Toepassen op selectie')}
                            </Button>
                        </Box>
                    )}

                    {/* Unsaved-edits bar — cell edits buffer here and only hit
                        the server when the user explicitly saves. */}
                    {currentTab.editable !== false && pendingRowCount > 0 && (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, mb: 2,
                            px: 1.5, py: 1, borderRadius: 1.5,
                            backgroundColor: isDark ? 'rgba(255,193,7,0.08)' : 'rgba(255,193,7,0.12)',
                            border: '1px solid', borderColor: isDark ? 'rgba(255,193,7,0.35)' : 'rgba(255,160,0,0.45)',
                        }}>
                            <Typography variant="body2" sx={{ flex: 1, color: textColor }}>
                                {pendingRowCount} {pendingRowCount === 1
                                    ? tr('cfg_data_pending_one', 'rij met onbewaarde wijzigingen')
                                    : tr('cfg_data_pending_many', 'rijen met onbewaarde wijzigingen')}
                            </Typography>
                            <Button size="small" disabled={savingEdits} onClick={discardTabEdits} sx={{ textTransform: 'none' }}>
                                {tr('cfg_data_discard', 'Annuleer wijzigingen')}
                            </Button>
                            <Button size="small" variant="contained" disabled={savingEdits} onClick={saveTabEdits} sx={{ textTransform: 'none' }}>
                                {savingEdits ? tr('cfg_data_saving', 'Bezig met opslaan…') : tr('cfg_data_save', 'Wijzigingen opslaan')}
                            </Button>
                        </Box>
                    )}

                    <Box sx={{ width: '100%', position: 'relative', minHeight: 200 }}>
                        {props.selectedWebshop && (
                            <>
                                {tabLoading[tabValue] && (
                                    <Box sx={{
                                        position: 'absolute', inset: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: isDark ? 'rgba(45,51,59,0.6)' : 'rgba(255,255,255,0.6)',
                                        zIndex: 2,
                                    }}>
                                        <CircularProgress size={28} />
                                    </Box>
                                )}
                                <DataGridPro
                                    // Remount per tab so checkbox selection and
                                    // edit state never leak between tables.
                                    key={tabValue}
                                    className={props.theme}
                                    rows={currentTab.rows}
                                    columns={currentTab.columns}
                                    autoHeight
                                    density="compact"
                                    rowHeight={40}
                                    pagination
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                                    disableRowSelectionOnClick
                                    checkboxSelection={currentTab.editable !== false}
                                    onRowSelectionModelChange={(model) => setRowSelection(model)}
                                    processRowUpdate={processRowUpdate}
                                    onProcessRowUpdateError={handleProcessRowUpdateError}
                                    // Highlight buffered (not-yet-saved) cells.
                                    getCellClassName={(params) =>
                                        pendingForTab[params.row?.id]?.[params.field] !== undefined ? 'sft-pending-cell' : ''}
                                    sx={{
                                        '& .sft-pending-cell': {
                                            backgroundColor: isDark ? 'rgba(255,193,7,0.16)' : 'rgba(255,193,7,0.22)',
                                        },
                                    }}
                                    isCellEditable={(params) => {
                                        // Belgium's shipping_costs / shipping_free_from
                                        // are defined elsewhere (processing settings) —
                                        // lock those two cells on the BE row so the
                                        // two surfaces can't drift apart. Keyed on the
                                        // stable `path` (labels are translated now).
                                        if (currentTab.path === '/countries' &&
                                            (params.field === 'shipping_costs' || params.field === 'shipping_free_from') &&
                                            params.row?.iso === 'BE') {
                                            return false;
                                        }
                                        return true;
                                    }}
                                />
                            </>
                        )}
                    </Box>
                </Box>
            </Paper>
        </div>
    );
};

export default WebshopDataScreen;
